import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { Button, IconButton, Paper, PaperProps } from '@mui/material';
import { FieldArray, useField } from 'formik';
import { isEqual, map } from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';

import { FormControls } from 'components/FormControls';
import { usePrevious } from 'hooks';

type ItemComponentProps = {
  index: number;
  item: any;
};

type Props = {
  name: string;
  defaultValue?: any;
  sortable?: boolean;
  removable?: boolean;
  addItemLabel?: React.ReactNode;
  ItemComponent: React.FC<ItemComponentProps>;
  paperPropsParams?: PaperProps & { sx?: { py?: number; px?: number } };
  AdditionalItemContent?: React.FC<ItemComponentProps>;
  onChange?: () => void;
  isItemHidden?: (item: any) => boolean;
};

export type { Props as FormikArrayProps };

export const FormikArray: React.FC<Props> = ({
  addItemLabel,
  name,
  sortable,
  removable,
  defaultValue,
  ItemComponent,
  paperPropsParams,
  AdditionalItemContent,
  onChange,
  isItemHidden,
}) => {
  const [field, , helpers] = useField(name);
  const changePosition = useCallback(
    (index: number, changeIndex: number) => {
      const newIndex = index + changeIndex;
      const maxIndex = field.value.length - 1;
      if (
        index < 0 ||
        index > maxIndex ||
        newIndex < 0 ||
        newIndex > maxIndex
      ) {
        return;
      }

      const newValue = [...field.value];
      const itemToChangePosition = newValue[index];
      newValue[index] = newValue[newIndex];
      newValue[newIndex] = itemToChangePosition;

      helpers.setValue(newValue);
    },
    [field, helpers],
  );

  const paperProps = useMemo(
    () => ({
      ...paperPropsParams,
      sx: {
        px: 4,
        py: 4,
        ...paperPropsParams?.sx,
      },
    }),
    [paperPropsParams],
  );

  const prevValue = usePrevious(field.value);
  useEffect(() => {
    if (onChange && !isEqual(prevValue, field.value)) {
      onChange();
    }
  }, [prevValue, field.value, onChange]);

  return (
    <FieldArray
      name={name}
      validateOnChange={false}
      render={(arrayHelpers) => (
        <FormControls>
          {map(field.value, (item, i: number) => {
            if (isItemHidden?.(item)) {
              return null;
            }

            return (
              <Paper key={i} {...paperProps}>
                <FormControls>
                  <div className="tw-flex tw-items-center">
                    <ItemComponent item={item} index={i} />
                    {sortable && (
                      <Fragment>
                        <IconButton
                          onClick={() => changePosition(i, 1)}
                          disabled={i === field.value.length - 1}
                          sx={{
                            opacity: i === field.value.length - 1 ? 0.3 : 1,
                          }}
                        >
                          <ArrowDownwardIcon color="action" />
                        </IconButton>
                        <IconButton
                          onClick={() => changePosition(i, -1)}
                          disabled={i === 0}
                          sx={{ opacity: i === 0 ? 0.3 : 1 }}
                        >
                          <ArrowUpwardIcon color="action" />
                        </IconButton>
                      </Fragment>
                    )}
                    {removable && (
                      <IconButton onClick={() => arrayHelpers.remove(i)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    )}
                  </div>
                  {AdditionalItemContent && (
                    <AdditionalItemContent item={item} index={i} />
                  )}
                </FormControls>
              </Paper>
            );
          })}
          {addItemLabel && (
            <div>
              <Button
                variant="outlined"
                onClick={() => arrayHelpers.push(defaultValue)}
              >
                {addItemLabel}
              </Button>
            </div>
          )}
        </FormControls>
      )}
    />
  );
};
